import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/auth/authService';
import { log } from '@/utils/core/vueLogger.js';
import { CacheManager } from '@/utils/core/cacheManager.js';
import { applyTranslations } from '@/i18n/legacyBridge';

const resourceCache = new CacheManager('VueCache', 'cache', {
    maxAge: 24 * 60 * 60 * 1000,
    memoryCacheTTL: 30 * 60 * 1000,
    logCategory: 'Resource',
    keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.resources.`,
});

// bootstrap.js's reconcile() is subscribed to 3 independent events (externis-ready,
// view-changed, user-changed) plus an immediate call — on a cold cache they can all
// fire before the first call populates resourceCache's memory cache, so every one of
// them races past the empty cache and fires its own network fetch (seen as dozens of
// duplicate ApplicationResources calls, worse under CPU throttling since that widens
// the race window). Share one in-flight request per locale/forceRefresh so concurrent
// callers await the same promise instead of stampeding the API.
const inFlightRequests = new Map();

export async function getApplicationResources(locale, forceRefresh = false) {
    const cacheKey = `resources.${locale}`;

    if (!forceRefresh) {
        const memCached = resourceCache.getFromMemory(cacheKey);
        if (memCached) return memCached;
    }

    const inFlightKey = `${locale}:${forceRefresh}`;
    if (inFlightRequests.has(inFlightKey)) return inFlightRequests.get(inFlightKey);

    const request = fetchApplicationResources(locale, forceRefresh).finally(() => inFlightRequests.delete(inFlightKey));
    inFlightRequests.set(inFlightKey, request);
    return request;
}

async function fetchApplicationResources(locale, forceRefresh) {
    const cacheKey = `resources.${locale}`;
    return resourceCache.fetchOrCache(cacheKey, () => fetchApplicationResourcesFromAPI(locale), {
        forceRefresh,
        fallback: [],
        backgroundRefresh: true,
        onHit: (resources) => applyTranslations(locale, resources),
    });
}

// Application resources are scoped per app token. Legacy fetched them once per app
// (ECOS=token, SALFO=tokenSales, DOC=tokenDoc) and accumulated. We replicate that:
// fetch with every currently-available token (ECOS first) and concatenate. buildOverlay
// then dedups with custom > default priority.
const RESOURCE_TOKEN_TYPES = ['token', 'tokenSales', 'tokenDoc'];

async function fetchApplicationResourcesFromAPI(locale) {
    try {
        // The API expects the 2-letter language code (e.g. 'fr'), not the full locale ('fr-FR').
        const apiLocale = locale.substr(0, 2);
        const url = getApiUrl(`Core/ApplicationResources?locale=${apiLocale}`);

        const user = iframeDataBridge.getCurrentUser() || {};
        const tokenTypes = RESOURCE_TOKEN_TYPES.filter((tt) => user[tt]);
        if (tokenTypes.length === 0) tokenTypes.push('token');

        const parts = await Promise.all(
            tokenTypes.map(async (tt) => {
                try {
                    const response = await authenticatedFetch(url, { method: 'GET' }, tt);
                    if (!response.ok) {
                        log('warn', 'Resource', `⚠️ ApplicationResources (${tt}) → HTTP ${response.status}`);
                        return [];
                    }
                    const json = await response.json();
                    return Array.isArray(json) ? json : [];
                } catch (e) {
                    log('warn', 'Resource', `⚠️ ApplicationResources (${tt}) failed: ${e.message}`);
                    return [];
                }
            })
        );

        const resources = parts.flat();
        applyTranslations(locale, resources);

        log('success', 'Resource', `✅ Fetched ${resources.length} application resources (${tokenTypes.join(', ')}) for locale ${locale}`);
        return resources;
    } catch (error) {
        log('error', 'Resource', `❌ Failed to fetch application resources from API: ${error.message}`);
        throw error;
    }
}

export async function refreshResources() {
    resourceCache.clearMemory();
    await resourceCache.clear();
}
